<template>
  <div class="setting-content-editor">
    <div v-for="(item, key) in filterSetting" class="setting" :class="CENTER_CONTENT_TYPE.includes(item.type) && 'is-center'" :key="key">
      <span class="label">{{ item.label }}</span>
      <template v-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.TEXT">
        <el-input v-model="data[key]" class="value" :disabled="isDisabled"></el-input>
        <span v-if="item.unit" class="unit">{{ item.unit }}</span>
      </template>
      <template v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.SELECT">
        <el-select v-model="data[key]" :disabled="isDisabled">
          <el-option
            v-for="option in item.options"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          ></el-option>
        </el-select>
      </template>
      <template v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.COLOR">
        <el-color-picker
          v-model="data[key]"
          class="value color-picker"
          :disabled="isDisabled"
        ></el-color-picker>
      </template>
      <template v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.IMAGE">
        <el-upload
          class="image-upload"
          action=""
          list-type="picture-card"
          accept=".jpg,.jpeg,.png,.gif"
          :class="isDisabled && 'is-disabled'"
          :auto-upload="false"
          :file-list="fileMap[key]"
          :on-change="(file) => handleImageChange(key, file)"
          :disabled="isDisabled"
        >
          <template #file="{ file }">
            <img class="picture" :src="file.url" alt="" />
            <span class="el-upload-list__item-actions">
              <span v-if="!isDisabled" class="el-upload-list__item-delete" @click="handleImageRemove(key)">
                <i class="el-icon-delete"></i>
              </span>
            </span>
          </template>
        </el-upload>
      </template>
      <div
        v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.POSITION"
        class="position-setting value"
      >
        <el-radio-group v-model="data[key]" class="radio-group">
          <template v-if="item.options">
            <el-radio v-for="(item) in item.options" :label="item.key" :key="item.key" :disabled="isDisabled">{{ item.label }}</el-radio>
          </template>
          <template v-else>
            <el-radio label="left" :disabled="isDisabled">{{ $t(`${i18nPrefix}.label.left`) }}</el-radio>
            <el-radio label="right" :disabled="isDisabled">{{ $t(`${i18nPrefix}.label.right`) }}</el-radio>
          </template>
        </el-radio-group>
      </div>
      <template v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.FONT">
        <FontEdit v-model="data[key]" ref="font" :isDisabled="isDisabled"></FontEdit>
      </template>
      <template v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.TEXTAREA">
        <el-input
          class="value"
          v-model="data[key]"
          type="textarea"
          :rows="3"
          :placeholder="item.placeholder"
          :disabled="isDisabled"
        ></el-input>
      </template>
      <template v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.SWITCH">
        <el-switch v-model="data[key]" class="switch" :disabled="isDisabled"></el-switch>
      </template>
      <template v-else-if="item.type === CUSTOM_SETTING_CONTENT_TYPE.LIST">
        <div class="list-setting">
          <draggable
            tag="div"
            filter=".is-disabled"
            class="list-group"
            :list="data[key]"
            handle=".drag-btn"
          >
            <div
              v-for="(menu, index) in data[key]"
              class="menu-item"
              :key="index"
            >
              <div class="drag-btn" :class="isDisabled && 'is-disabled'">
                <i class="iconfont iconMore"></i>
              </div>
              <div class="menu-label">
                <el-input v-model="menu.label" :disabled="isDisabled" :placeholder="$t(`${i18nPrefix}.label.cnName`)"></el-input>
              </div>
              <div v-if="item.supportEnglish" class="menu-label">
                <el-input v-model="menu.enLabel" :disabled="isDisabled" :placeholder="$t(`${i18nPrefix}.label.enName`)"></el-input>
              </div>
              <div class="menu-label">
                <el-input class="link-input" v-model="menu.link" :disabled="isDisabled" :placeholder="$t(`${i18nPrefix}.label.link`)"></el-input>
              </div>
              <div class="btn-group">
                <i
                  class="delete-btn iconfont iconRemove"
                  :class="isDisabled && 'is-disabled'"
                  @click="handleDeleteMenu(key, index)"
                ></i>
                <i
                  class="add-btn iconfont iconAdd1"
                  :class="isDisabled && 'is-disabled'"
                  @click="handleAddMenu(key)"
                ></i>
              </div>
            </div>
          </draggable>
          <div v-if="data[key].length === 0" class="default-wrapper">
            <i
              class="add-btn iconfont iconAdd1"
              :class="isDisabled && 'is-disabled'"
              @click="handleAddMenu(key)"
            ></i>
            <div v-if="item.defaultList" class="default-list">
              <el-input v-for="(name, index) in item.defaultList" disabled :key="index" :value="name"></el-input>
            </div>
          </div>
        </div>
      </template>
      <slot :name="item.slotName" :data="data[key]" :item="item"></slot>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { CUSTOM_SETTING_CONTENT_TYPE } from '@/constant/company'
import draggable from 'vuedraggable'
import FontEdit from './fontEdit.vue'
import { getFilePath, uploadFile } from '@/utils/qiniu'
import { mapGetters } from 'vuex'

const CENTER_CONTENT_TYPE = [
  CUSTOM_SETTING_CONTENT_TYPE.COLOR,
  CUSTOM_SETTING_CONTENT_TYPE.SWITCH
]

export default {
  components: {
    draggable,
    FontEdit
  },
  props: {
    data: {
      required: true
    },
    setting: {
      required: true
    },
    settingType: {
      type: String
    },
    isDisabled: {
      type: Boolean,
      default: true
    }
  },
  model: {
    prop: 'data',
    event: 'change'
  },
  data () {
    return {
      CUSTOM_SETTING_CONTENT_TYPE,
      CENTER_CONTENT_TYPE,
      i18nPrefix: 'admin.company.customCompanyTheme',
      fileMap: {}
    }
  },
  computed: {
    filterSetting () {
      const filterSetting = {}
      _.forEach(this.setting, (item, key) => {
        if (item.hidden) {
          return
        }
        filterSetting[key] = item
      })
      return filterSetting
    }
  },
  created () {
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    handleDeleteMenu (dataKey, index) {
      if (this.isDisabled) {
        return
      }
      this.data[dataKey].splice(index, 1)
    },
    handleAddMenu (dataKey) {
      if (this.isDisabled) {
        return
      }
      this.data[dataKey].push({ label: null, enLabel: null, link: null })
    },
    initImageUrl () {
      _.forEach(this.setting, (item, key) => {
        if (item.type === CUSTOM_SETTING_CONTENT_TYPE.IMAGE) {
          if (this.data[key]) {
            this.$set(this.fileMap, key, [this.data[key]])
          }
        }
      })
    },
    handleImageChange (key, file) {
      this.fileMap[key] = [{
        file,
        url: file.url
      }]
    },
    handleImageRemove (dataKey) {
      this.$set(this.fileMap, dataKey, [])
      this.$emit('change', {
        ...this.data,
        [dataKey]: null
      })
    },
    generateFileKey (contentType, contentItemId) {
      const account = this.getCurrentAccount()
      const fileKey = `job/custom_content/${contentType}/${contentItemId}/${account.id}/${new Date().valueOf()}`
      return fileKey
    },
    async uploadImage () {
      const allPromiseList = []
      _.forEach(this.fileMap, (fileList, key) => {
        if (fileList.length > 0 && fileList[0].file) {
          allPromiseList.push(this.uploadFile(fileList[0].file.raw, key))
        }
      })
      _.forEach(this.$refs.font, (fontRef) => {
        allPromiseList.push(...fontRef.uploadFont())
      })

      return Promise.all(allPromiseList)
    },
    async uploadFile (file, dataKey) {
      const fileKey = this.generateFileKey(this.settingType, dataKey)
      await uploadFile(file, fileKey)
      getFilePath(fileKey)
      this.$emit('change', {
        ...this.data,
        [dataKey]: {
          url: getFilePath(fileKey)
        }
      })
    }
  },
  watch: {
    setting () {
      this.initImageUrl()
    }
  }
}
</script>

<style scoped lang="scss">
.setting-content-editor {
  .setting {
    display: flex;
    margin-bottom: 10px;

    &.is-center {
      align-items: center;
    }

    .label {
      font-size: 14px;
      color: #606266;
      line-height: 32px;
      width: 20%;
      margin-right: 20px;
    }

    .unit {
      line-height: 28px;
      margin-left: 10px;
    }

    .value {
      width: 20%;
    }

    .color-picker {
      height: 32px;

      ::v-deep {
        .el-color-picker__trigger {
          width: 32px;
          height: 32px;
        }
      }
    }

    .switch {
      height: 32px;
    }

    .image-upload {
      min-width: 56px;
      height: 56px;
      overflow: hidden;

      .picture {
        width: 100%;
        height: 100%;
      }

      &.is-disabled {
        ::v-deep {
          .el-upload {
            cursor: not-allowed;
          }

          .el-upload-list__item-actions {
            cursor: not-allowed;
          }
        }
      }

      ::v-deep {
        .el-upload-list {
          .el-upload-list__item {
            width: auto;
            height: 100%;
          }
        }

        .el-upload {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }

    .position-setting {
      display: flex;
      flex-direction: column;

      .radio-group {
        .el-radio {
          line-height: 32px;
        }
      }
    }

    .list-setting {
      .menu-item {
        display: flex;
        align-items: center;
        margin-bottom: 10px;

        &:last-child {
          margin-bottom: 0;
        }

        .menu-label {
          display: flex;
          align-items: center;
          margin-right: 10px;

          .name {
            flex-shrink: 0;
            margin-right: 10px;
          }

          .link-input {
            width: 300px;
          }
        }

        .drag-btn {
          margin-right: 10px;
          cursor: pointer;

          &.is-disabled {
            cursor: not-allowed;
          }
        }

        .btn-group {
          .delete-btn {
            margin-right: 10px;
            color: red;
            cursor: pointer;

            &.is-disabled {
              color: rgba($color: red, $alpha: 0.7);
              cursor: not-allowed;
            }
          }

          .add-btn {
            color: #0096cc;
            cursor: pointer;

            &.is-disabled {
              color: rgba($color: #0096cc, $alpha: 0.7);
              cursor: not-allowed;
            }
          }
        }
      }

      .default-wrapper {
        .add-btn {
          line-height: 32px;
          height: 32px;
          color: #0096cc;
          cursor: pointer;

          &.is-disabled {
              color: rgba($color: #0096cc, $alpha: 0.7);
              cursor: not-allowed;
            }
        }

        .default-list {
          .el-input {
            margin-bottom: 4px;
          }
        }
      }
    }
  }
}
</style>
